import { ACTIONS, can, type Action } from "./auth/roles";
import type { Principal } from "./types";

export type FireRouteId =
  | "intake"
  | "board"
  | "incidents"
  | "resources"
  | "premises"
  | "certificates"
  | "inspections"
  | "hydrants"
  | "approvals"
  | "reports"
  | "access";

export interface FireRoute {
  id: FireRouteId;
  /** Relative to the module's basePath ("" is the module home). */
  path: string;
  title: string;
  /** The screen's reads: it is shown when the signed-in person may call at least one (local sessions included). */
  actions: readonly Action[];
  /** Any one of these roles shows the route (the union of its actions' roles). */
  requiredRoles: readonly string[];
}

function route(id: FireRouteId, path: string, title: string, actions: Action[]): FireRoute {
  return { id, path, title, actions, requiredRoles: Array.from(new Set(actions.flatMap((a) => ACTIONS[a].roles))) };
}

export const routes: readonly FireRoute[] = [
  route("intake", "", "Intake queue", ["viewAlerts"]),
  route("board", "board", "Dispatch board", ["viewLiveMap"]),
  route("incidents", "incidents", "Incidents and reports", ["viewIncidents"]),
  route("resources", "resources", "Stations and appliances", ["viewStations", "viewAppliances", "viewAssignments"]),
  route("premises", "premises", "Premises", ["viewPremises"]),
  route("certificates", "certificates", "Certificates and fees", ["viewApplications", "viewCertificates"]),
  route("inspections", "inspections", "Inspections", ["viewInspections", "viewHeldItems"]),
  route("hydrants", "hydrants", "Hydrants", ["viewHydrants"]),
  route("approvals", "approvals", "Approvals", ["viewApprovals"]),
  route("reports", "reports", "Response times", ["viewResponseTimes", "viewSnapshot"]),
  route("access", "access", "Local sign-in", ["viewMyCredentials", "viewAllCredentials", "resetCredential"]),
];

/** The routes this person may open now (a FER local session sees only screens with `x-local-auth` reads). */
export function visibleRoutes(principal: Pick<Principal, "roles" | "auth">): FireRoute[] {
  return routes.filter((r) => r.actions.some((a) => can(principal, a)));
}

export interface HubModule {
  code: string;
  title: string;
  basePath: string;
  routes: readonly FireRoute[];
  requiredRoles: readonly string[];
  featureFlag: string;
}

/** Registration record for the Hub shell (clients/hub-web). */
export const fireModule: HubModule = {
  code: "fer",
  title: "Fire & Emergency",
  basePath: "/fire",
  routes,
  // Any role that opens at least one screen.
  requiredRoles: Array.from(new Set(routes.flatMap((r) => r.requiredRoles))),
  featureFlag: "fer.module",
};

export function routeForPath(pathname: string, basePath = fireModule.basePath): FireRouteId | null {
  const rel = pathname.startsWith(basePath) ? pathname.slice(basePath.length).replace(/^\/+|\/+$/g, "") : null;
  if (rel === null) return null;
  return routes.find((r) => r.path === rel)?.id ?? null;
}

export function pathForRoute(id: FireRouteId, basePath = fireModule.basePath): string {
  const r = routes.find((x) => x.id === id);
  return r && r.path ? `${basePath}/${r.path}` : basePath;
}
